// Copyright (c) 2025 by Audere

import React, { useEffect, useState } from "react";
import Sidebar from "./Sidebar";
import MainPanel from "./MainPanel";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useSelector } from "react-redux";
import {
  LabelingStatus,
  MonitorConversationData,
} from "../features/api/labelApiSlice";
import {
  setConvoLabelers,
  setLabeler,
  setQC,
} from "../features/slices/labelSlice";
import { ConvoLabelers } from "./LabelerSelect";
import { useAppDispatch, useAppSelector } from "../features/store";

import { selectCurrentEmail } from "../features/slices/authSlice";
import { useLabelingSchema } from "../hooks/useSchema";
import { conversationLimit } from "../constants/conversation";
import { useSampledConversations } from "../hooks/useSampledConversations";
import { useCurrentUser } from "../hooks/useCurrentUser";
import { useConversationForMonitor } from "../hooks/useConversationForMonitor";
import { useLabelsForMonitor } from "../hooks/useLabelsForMonitor";

const MonitorConversations: React.FC = () => {
  const { conversationUID, labelTypeID } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [allConversations, setAllConversations] = useState<
    MonitorConversationData[]
  >([]);
  const pageParam = searchParams.get("page");
  const pageValue =
    pageParam && !isNaN(Number(pageParam)) && Number(pageParam) > 0
      ? Number(pageParam)
      : 1;
  const [offsetValue, setOffsetValue] = useState(
    (pageValue - 1) * conversationLimit
  );
  const [hasMore, setHasMore] = useState(true);

  const {
    conversations: sampledConversations,
    offset: sampledOffset,
    endOfList: sampledEndOfList,
    isLoading: isLoadingSampled,
    error: sampledError,
  } = useSampledConversations(labelTypeID ?? "", offsetValue);
  const { labels: existingMonitorLabels } = useLabelsForMonitor(
    conversationUID ?? ""
  );
  const userEmail = useSelector(selectCurrentEmail);
  const dispatch = useAppDispatch();
  const { isMonitor } = useCurrentUser();
  const {
    conversation: monitorConversationMessages,
    isLoading: monitorConvoIsLoading,
    error: monitorConvoError,
  } = useConversationForMonitor(conversationUID ?? "", labelTypeID ?? "");

  const conversation = allConversations.find(
    (c) => c.conversation_uid === conversationUID
  );
  const convoLabelers = useAppSelector((state) => state.label.convoLabelers);
  const { labelingTasks, labelingTypes } = useLabelingSchema();

  useEffect(() => {
    if (isMonitor && sampledConversations.length > 0) {
      setAllConversations(sampledConversations);
    }
  }, [isMonitor, sampledConversations]);

  useEffect(() => {
    if (sampledEndOfList && sampledOffset > 0) {
      setHasMore(true);
    } else {
      setHasMore(!sampledEndOfList);
    }
  }, [sampledEndOfList, sampledOffset]);

  const handleLoadMore = (value: "next" | "prev") => {
    if (!isLoadingSampled) {
      setAllConversations([]);
      let newOffset = offsetValue;

      if (value === "next") {
        newOffset = offsetValue + conversationLimit;
      } else {
        newOffset = Math.max(offsetValue - conversationLimit, 0);
      }

      const newPage = Math.floor(newOffset / conversationLimit) + 1;
      searchParams.set("page", String(newPage));
      setSearchParams(searchParams);
      setOffsetValue(newOffset);
      navigate({
        pathname: location.pathname,
        search: `?${searchParams.toString()}`,
      });
    }
  };

  useEffect(() => {
    if (!pageParam || isNaN(Number(pageParam)) || Number(pageParam) <= 0) {
      searchParams.set("page", "1");
      setSearchParams(searchParams);
      navigate(
        { pathname: location.pathname, search: `?${searchParams.toString()}` },
        { replace: true }
      );
    }
  }, [location.pathname, navigate, searchParams, setSearchParams, pageParam]);
  useEffect(() => {
    if (allConversations.length > 0) {
      const initConvoLabelers = allConversations.reduce<ConvoLabelers>(
        (acc, convo) => {
          const labelers = convo.labelers.filter(
            (labeler) =>
              labeler.labeler_status === LabelingStatus.label_complete
          );
          const inProgressLabelers = convo.labelers.filter(
            (labeler) =>
              labeler.labeler_status === LabelingStatus.label_in_progress
          );
          const existing = convoLabelers?.[convo.conversation_uid];
          acc[convo.conversation_uid] = {
            labelers,
            selectedLabeler: existing?.selectedLabeler ?? labelers[0],
            inProgressLabelers,
          };
          return acc;
        },
        { ...(convoLabelers ?? {}) }
      );
      if (JSON.stringify(convoLabelers) !== JSON.stringify(initConvoLabelers)) {
        dispatch(setConvoLabelers(initConvoLabelers));
      }
    }
  }, [allConversations, dispatch, labelTypeID, convoLabelers]);
  useEffect(() => {
    if (conversationUID && convoLabelers && conversationUID in convoLabelers) {
      const foundQC = conversation?.qc.find((qc) => qc.email === userEmail);
      const qcComplete = conversation?.qc.filter(
        (qc) =>
          qc.labeler_uid ===
            convoLabelers[conversationUID].selectedLabeler?.labeler_uid &&
          qc.qc_status === LabelingStatus.qc_complete
      );
      dispatch(setLabeler(convoLabelers[conversationUID].selectedLabeler));
      dispatch(setQC(isMonitor ? qcComplete?.[0] : foundQC));
    }
  }, [
    convoLabelers,
    conversationUID,
    dispatch,
    conversation,
    userEmail,
    labelTypeID,
    existingMonitorLabels,
    labelingTasks,
    labelingTypes,
    isMonitor,
  ]);
  return (
    <>
      <Sidebar
        isLoading={isLoadingSampled}
        error={sampledError}
        conversations={allConversations}
        onLoadMore={handleLoadMore}
        hasMore={hasMore}
        endOfList={!!sampledEndOfList}
        conversationMessages={monitorConversationMessages}
      />
      <MainPanel
        isLoading={isLoadingSampled}
        error={sampledError}
        conversationMessages={monitorConversationMessages}
        convoIsLoading={monitorConvoIsLoading}
        convoError={monitorConvoError}
      />
    </>
  );
};

export default MonitorConversations;
